import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

let SDK = null
let loading = false
let currentUserId = null

//Check whether Braze debug logging is enabled.
const isBrazeDebugEnabled = () => localStorage.getItem('BRAZE_ENABLE_LOGGING') === 'true'

//Expose limited Braze debug helpers globally for browser console testing.
function exposeBrazeDebug(braze) {
    if (!isBrazeDebugEnabled()) return

    window.BrazeDebug = {
        logCustomEvent: (...args) => braze.logCustomEvent(...args)
    }
}

// Remove Braze debug helpers from the global window object.
function cleanupBrazeDebug() {
    if (window.BrazeDebug) {
        delete window.BrazeDebug
    }
}

/**
 * Initialize Braze SDK and optionally attach a logged-in user.
 *
 * @param {string} [userId] - Logged-in customer identifier.
 * @returns {Promise<object|null>} Braze SDK instance or null if initialization fails.
 */
export async function initBraze(userId) {
    if (SDK) {
        if (userId && userId !== currentUserId) {
            SDK.changeUser(userId)
            currentUserId = userId
        }

        return SDK
    }

    // Prevent duplicate SDK initialization
    if (loading) return null

    const {app} = getConfig()

    if (app.IS_BRAZE_ENABLED !== 'true' || !app.BRAZE_API_KEY || !app.BRAZE_BASE_URL) {
        return null
    }

    loading = true

    try {
        const braze = await import('@braze/web-sdk')

        // Initialize Braze SDK
        braze.initialize(app.BRAZE_API_KEY, {
            baseUrl: app.BRAZE_BASE_URL,
            enableLogging: isBrazeDebugEnabled(),
            allowUserSuppliedJavascript: true
        })

        // Must run before openSession so session-start / trigger IAMs can display
        braze.automaticallyShowInAppMessages()

        // Attach logged-in user to Braze profile
        if (userId) {
            braze.changeUser(userId)
            currentUserId = userId
        }

        // Start Braze session
        braze.openSession()
        SDK = braze
        window.Braze = SDK

        // Expose limited debug API only when logging is enabled
        exposeBrazeDebug(braze)

        return SDK
    } catch (err) {
        console.error('Braze failed to initialize', err)
        return null
    } finally {
        loading = false
    }
}

/**
 * Log a Braze custom event.
 *
 * @param {string} eventName - Braze custom event name.
 * @param {object} [properties] - Optional event properties.
 * @returns {void}
 */
export function logBrazeEvent(eventName, properties) {
    SDK?.logCustomEvent(eventName, properties)
    SDK && isBrazeDebugEnabled() && console.log('Braze -', eventName, properties)
}

/**
 * Destroy the active Braze SDK session and cleanup references.
 *
 * @returns {void}
 */
export function stopBraze() {
    if (!SDK) return

    try {
        // Destroy active Braze session
        SDK.destroy()
    } catch (err) {
        console.error('Braze shutdown failed', err)
    }

    SDK = null
    currentUserId = null

    cleanupBrazeDebug()
}
